import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog.js';
import { Button } from '@/components/ui/button.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { bulkUpdateSessionFeedbacks, getSessionFeedbacks } from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import { GetSessionFeedbacksFilter, SessionFeedback } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';

interface BulkUpdateLateFeedbackProps {
  lateFeedbackDismissed: boolean;
  trigger: ReactNode;
  selectedFeedbacks: SessionFeedback[];
  onSuccess?: () => void;
  isSelectAllPagesActive?: boolean;
  filters?: GetSessionFeedbacksFilter;
}

export const BulkUpdateLateFeedback = ({
  lateFeedbackDismissed,
  trigger,
  selectedFeedbacks,
  onSuccess,
  isSelectAllPagesActive = false,
  filters,
}: BulkUpdateLateFeedbackProps) => {
  const confirmationMessage = lateFeedbackDismissed
    ? 'This update will dismiss late feedback for selected session feedbacks. Are you sure you want to proceed?'
    : 'This update will restore late feedback (if applicable) for selected session feedbacks. Are you sure you want to proceed?';

  const [open, setOpen] = useState(false);
  const actionableFeedbacks = selectedFeedbacks.filter(({ feedbackSubmitted }) => feedbackSubmitted);
  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => {
      // Use filters when selecting all pages, otherwise use specific feedbacks
      if (isSelectAllPagesActive && filters) {
        return bulkUpdateSessionFeedbacks({
          filters: {
            ...filters,
            feedbackSubmitted: true, // Only update feedbacks that have been submitted
          },
          options: {
            lateFeedbackDismissed,
          },
        });
      }

      return bulkUpdateSessionFeedbacks(
        actionableFeedbacks.map(({ sessionId, studentId, coachId }) => {
          return {
            sessionId,
            studentId,
            coachId,
            lateFeedbackDismissed: lateFeedbackDismissed,
            assessmentResults: [],
            action: 'adminManualAction',
          };
        }),
      );
    },
  });

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const onSubmit = () => {
    return mutateAsync()
      .then(({ successSessionIds, errors }) => {
        toast({
          title: 'Updated',
          description: `Successfully updated late feedback status for ${successSessionIds.length} feedbacks.`,
        });

        if (errors.length > 0) {
          toast({
            title: 'Some sessions failed to update',
            description: (
              <div>
                <ul>
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            ),
            variant: 'destructive',
          });
        }

        queryClient.invalidateQueries(getSessionFeedbacks());
        setOpen(false);
        onSuccess?.();
      })
      .catch((e: unknown) => {
        toast({
          title: 'Error updating late feedback',
          description: airServerErrorFrom(e)?.userFacingMessage,
          variant: 'destructive',
        });
      });
  };

  return (
    <>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger>{trigger}</AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update late feedback</AlertDialogTitle>
            <AlertDialogDescription>{confirmationMessage}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction>
              <Button
                loading={isPending}
                onClick={(e) => {
                  e.preventDefault();
                  onSubmit();
                }}
              >
                Update
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
